/** @format */

import { ComponentRef, Injectable, Type, ViewContainerRef } from '@angular/core';

@Injectable({
	providedIn: 'root'
})
export class ComponentHandlerService {
	// Variable will store all the references of components created dynamically
	private componentRefs: ComponentRef<unknown>[] = [];

	/*
		Usage cases:
		this.service.add(MyContainer, MyComponent); // Will create MyComponent and attach it to the MyContainer
		this.service.add(MyContainer, MyComponent, { key: 'propKey', value: 40 }); // Will create MyComponent and set some properties
	 */
	add<T>(
		viewContainerRef: ViewContainerRef,
		component: Type<T> | ComponentRef<T>,
		properties: { key: string; value: unknown }[] = []
	): ComponentRef<T> {
		// clear dynamic components shown in the container previously
		viewContainerRef.clear();

		const componentRef =
			component instanceof ComponentRef
				? component
				: viewContainerRef.createComponent(component);

		(<{ [key: string]: unknown }>componentRef.instance).selfReference = componentRef;

		if (properties.length) {
			properties.forEach(({ key, value }) => {
				(<{ [key: string]: unknown }>componentRef.instance)[key] = value;
			});
		}

		this.componentRefs.push(componentRef);

		return componentRef;
	}

	/*
		Usage cases:
		this.service.remove(crIndex); // Remove by index or
		this.service.remove(cr); // Remove by reference or
	 */
	remove(component: number | ComponentRef<unknown>): boolean {
		const isIndexValue = typeof component === 'number';

		if (isIndexValue && this.componentRefs.length < component) {
			return false;
		}

		let componentRef: ComponentRef<unknown>;

		const componentIndex = isIndexValue
			? component
			: this.componentRefs.findIndex((componentStored) => componentStored === component);

		if (componentIndex >= 0) {
			componentRef = this.componentRefs[componentIndex];
		}

		return this.removeComponent(componentRef, componentIndex);
	}

	// Usage case: this.service.clear(); // Remove all dynamically created components
	clear(): void {
		while (this.componentRefs.length > 0) {
			const index = this.componentRefs.length - 1;
			const component = this.componentRefs[index];

			this.removeComponent(component, index);
		}
	}

	getIndex(componentRef: ComponentRef<unknown>): number {
		return this.componentRefs.indexOf(componentRef);
	}

	private removeComponent(componentRef: ComponentRef<unknown>, componentIndex = -1): boolean {
		const wasComponentFound = componentRef && componentIndex > -1;

		if (wasComponentFound) {
			componentRef.destroy();
			this.componentRefs.splice(componentIndex, 1);
		}

		return wasComponentFound;
	}
}
